import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutGrid, List, Search } from "lucide-react";

import { useInventory } from "@/api/queries";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Page } from "@/layout/Page";
import { Pagination } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { Input } from "@/components/Input";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { stockLabel, stockTone } from "@/lib/stock";
import { PageHeader } from "@/layout/PageHeader";
import { formatQuantity, formatUnit } from "@/lib/format";

const PAGE_SIZE = 48;

/**
 * The catalogue (Frontend Spec §8).
 *
 * A photo grid, because for parts the eye is faster than the part number. The
 * dense table for administration lives on /inventory.
 */
export function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(term);
  const [view, setView] = useState<"grid" | "list">("grid");
  const offset = Number(params.get("offset") ?? 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const { data, isLoading, error, refetch } = useInventory({
    q: debounced || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  function setOffset(next: number) {
    const merged = new URLSearchParams(params);
    merged.set("offset", String(next));
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Produits"
        subtitle="Le catalogue. Un produit est une définition — l'exemplaire physique est un asset."
        actions={
          <div className="flex gap-1 rounded-md border border-border p-0.5">
            <Button
              variant={view === "grid" ? "subtle" : "ghost"}
              size="sm"
              onClick={() => setView("grid")}
              aria-label="Vue grille"
              aria-pressed={view === "grid"}
            >
              <LayoutGrid className="size-4" aria-hidden />
            </Button>
            <Button
              variant={view === "list" ? "subtle" : "ghost"}
              size="sm"
              onClick={() => setView("list")}
              aria-label="Vue liste"
              aria-pressed={view === "list"}
            >
              <List className="size-4" aria-hidden />
            </Button>
          </div>
        }
      />

      <div className="relative mb-3">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
          aria-hidden
        />
        <Input
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            const merged = new URLSearchParams(params);
            merged.set("q", event.target.value);
            merged.delete("offset");
            setParams(merged, { replace: true });
          }}
          placeholder="Nom, fabricant, MPN, SKU ou code-barres…"
          className="pl-9"
          aria-label="Rechercher un produit"
        />
      </div>

      {isLoading && (
        <Card>
          <TableSkeleton rows={6} columns={5} />
        </Card>
      )}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && data.items.length === 0 && (
        <Card>
          <EmptyState
            title="Aucun produit"
            description="Le catalogue est vide ou aucun produit ne correspond à la recherche."
          />
        </Card>
      )}

      {data && data.items.length > 0 && view === "grid" && (
        <>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
            {data.items.map((row) => (
              <li key={row.product_id}>
                <Link
                  to={`/products/${row.product_id}`}
                  className="flex h-full flex-col gap-2 rounded-lg border border-border bg-surface p-2 transition-colors hover:border-border-strong hover:bg-surface-hover"
                >
                  <Photo
                    mediaId={row.media_id}
                    alt={row.name}
                    className="aspect-square w-full"
                    rounded="rounded-md"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-[13px] font-medium text-ink">{row.name}</p>
                    <p className="truncate text-[11px] text-ink-subtle">
                      {[row.manufacturer, row.part_number].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="tnum text-[13px] font-semibold text-ink">
                      {formatQuantity(row.available_quantity)}{" "}
                      <span className="text-[10px] font-normal text-ink-subtle">
                        {formatUnit(row.unit)}
                      </span>
                    </span>
                    <StatusBadge tone={stockTone(row.available_quantity, row.book_quantity)}>
                      {stockLabel(row.available_quantity, row.book_quantity)}
                    </StatusBadge>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <Card className="mt-3">
            <Pagination
              offset={data.offset}
              limit={data.limit}
              total={data.total}
              onChange={setOffset}
            />
          </Card>
        </>
      )}

      {data && data.items.length > 0 && view === "list" && (
        <Card>
          <ul className="divide-y divide-border/60">
            {data.items.map((row) => (
              <li key={row.product_id}>
                <Link
                  to={`/products/${row.product_id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover"
                >
                  <Photo mediaId={row.media_id} alt={row.name} className="size-11 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] text-ink">{row.name}</span>
                    <span className="block truncate text-[12px] text-ink-subtle">
                      {[row.manufacturer, row.part_number, row.sku].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="tnum shrink-0 text-[15px] font-semibold text-ink">
                    {formatQuantity(row.available_quantity)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Pagination
            offset={data.offset}
            limit={data.limit}
            total={data.total}
            onChange={setOffset}
          />
        </Card>
      )}
    </Page>
  );
}
